import { useEffect, useCallback } from 'react';
import { prop, path } from 'ramda';
import { ILaunchGameEventData } from 'js/hooks/gameLauncher/types';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useAppSelector, useUserSelector, useDeviceSelector } from 'js/store/hooks';

/**
 * Event fires when game was launched.
 * @memberOf Playtech.API.events
 * @event GAME_LAUNCH
 * @type {callback}
 * @param {Object[]} details
 * @param {string} details[0].casinoName IMS brand name (IMS casinoName). Corresponding BI parameter: "brand".
 * @param {string} [details[0].categoryName] Title of the category. Corresponding BI parameter: "cat_name".
 * @param {number} [details[0].categoryPosition] Position of the category (if present) on page (from top to the bottom). Corresponding BI parameter: "cat_pos".
 * @param {string} details[0].container Name of a place/component the game was launched from (Games grid, Banner, Leaderboard etc.). Corresponding BI parameter: "launch_ctx".
 * @param {string} details[0].gameCode Launch game code. Corresponding BI parameter: "launch_code".
 * @param {string} [details[0].gameFeed] Feed name. Corresponding BI parameter: "feed_name".
 * @param {string} details[0].gameName Game name
 * @param {number} [details[0].gamePositionInCategory] Position of the game in the category (if present). Corresponding BI parameter: "game_pos".
 * @param {string} details[0].launchType Game launch type (Real/Demo)
 * @param {string} [details[0].pggCategoryName] PGG category name. Corresponding BI parameter: "pgg_cat_name".
 * @param {string} details[0].platform Platform (desktop/tablet/mobile). Corresponding BI parameter: "cplat".
 * @param {string} [details[0].tidPgg] "tid" value from API PGG response (if present). Corresponding BI parameter: "tid".
 * @param {string} [details[0].userId] IMS player userId (for logged-in users). Corresponding BI parameter: "user_id".
 * @example Playtech.on('GAME_LAUNCH', ({ gameCode, container, gameName, game_pos }) => { ... });
 */
export const GAME_LAUNCH = 'GAME_LAUNCH';

export const useOnGameLaunch = (eventTarget: EventTarget) => {
    const casinoName = useAppSelector(prop('casinoName'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const formFactor = useDeviceSelector(prop('formFactor'));
    const userId = useUserSelector(path(['userDetails', 'userId']));

    const trackGameLaunch = useCallback(({ game, mode, options }: ILaunchGameEventData) => {
        const { gameCode, name } = game;
        const {
            gameFeed, gameName, container = 'Undefined', categoryName,
            gamePositionInCategory, categoryPosition, tidPgg, pggCategoryName
        } = options || {};
        const details = {
            casinoName,
            container,
            gameCode,
            gameName: gameName || name,
            launchType: mode ? 'Real' : 'Demo',
            platform: formFactor,
            ...isLoggedIn && { userId },
            // only for Games portlets
            ...categoryName && { categoryName },
            ...gameFeed && { gameFeed },
            ...tidPgg && { tidPgg },
            ...pggCategoryName && { pggCategoryName },
            ...typeof categoryPosition === 'number' && categoryPosition !== -1 && { categoryPosition },
            ...typeof gamePositionInCategory === 'number' && gamePositionInCategory !== -1 && { gamePositionInCategory }
        };

        const event = new CustomEvent(GAME_LAUNCH, { detail: [details] });

        eventTarget.dispatchEvent(event);
    }, [eventTarget, casinoName, formFactor, userId, isLoggedIn]);

    useEffect(() => {
        publicEventsAPIService.on('game-launch-event', trackGameLaunch);

        return () => {
            publicEventsAPIService.off('game-launch-event', trackGameLaunch);
        };
    }, [trackGameLaunch]);
};
